import { useCallback, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  githubPullRequestsApiRef,
  type GithubPullRequestsApi,
} from '@roadiehq/backstage-plugin-github-pull-requests';
import {
  Alert,
  Box,
  Button,
  Flex,
  Link,
  Skeleton,
  TablePagination,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import {
  RiChat3Line,
  RiGitClosePullRequestLine,
  RiGitMergeLine,
  RiGitPullRequestLine,
} from '@remixicon/react';
import { OPEN_PULL_REQUESTS_QUERY } from './githubOrg';
import { HomeCard } from './HomeCard';
import { searchPullRequests } from './searchPullRequests';
import { useAsyncValue } from './useAsyncValue';
import { useGitHubSignIn } from './useGitHubSignIn';

const GITHUB_URL = 'https://github.com';

type PullRequest = Awaited<
  ReturnType<GithubPullRequestsApi['searchPullRequest']>
>[number];

/** Status icon in GitHub's colours, on BUI's status and brand tokens. */
const StatusIcon = ({ pr }: { pr: PullRequest }) => {
  let label = 'Open';
  let Icon = RiGitPullRequestLine;
  let color = 'var(--bui-fg-success)';
  if (pr.state === 'open' && pr.draft) {
    label = 'Draft';
    color = 'var(--bui-fg-secondary)';
  } else if (pr.merged) {
    label = 'Merged';
    Icon = RiGitMergeLine;
    color = 'var(--make-fg-brand)';
  } else if (pr.state === 'closed') {
    label = 'Closed';
    Icon = RiGitClosePullRequestLine;
    color = 'var(--bui-fg-danger)';
  }
  return (
    <Icon
      size={16}
      role="img"
      aria-label={label}
      style={{ color, flexShrink: 0 }}
    />
  );
};

const repoName = (repositoryUrl: string) =>
  new URL(repositoryUrl).pathname
    .split('/')
    .filter(Boolean)
    .slice(1, 3)
    .join('/');

const PullRequestRow = ({ pr }: { pr: PullRequest }) => (
  <Flex
    align="start"
    gap="2"
    py="2"
    px="4"
    style={{ borderBottom: '1px solid var(--bui-border-1)' }}
  >
    <Box pt="0.5">
      <StatusIcon pr={pr} />
    </Box>
    <Box style={{ minWidth: 0, flex: 1 }}>
      <Text
        as="p"
        variant="body-large"
        weight="bold"
        truncate
        style={{ minWidth: 0 }}
      >
        <Link
          href={pr.htmlUrl}
          target="_blank"
          rel="noopener noreferrer"
          standalone
          color="secondary"
          style={{ paddingRight: 4 }}
        >
          {repoName(pr.repositoryUrl)}
        </Link>
        <Link
          href={pr.pullRequest.htmlUrl ?? pr.htmlUrl}
          target="_blank"
          rel="noopener noreferrer"
          standalone
          color="primary"
        >
          {pr.title}
        </Link>
      </Text>
      <Text as="p" variant="body-small" color="secondary">
        #{pr.number} opened by{' '}
        <Link
          href={pr.user.htmlUrl ?? GITHUB_URL}
          target="_blank"
          rel="noopener noreferrer"
          standalone
          variant="body-small"
          color="secondary"
        >
          {pr.user.login}
        </Link>
      </Text>
      <Text as="p" variant="body-small" color="secondary">
        {pr.pullRequest.created_at}
      </Text>
    </Box>
    <Link
      href={pr.htmlUrl}
      target="_blank"
      rel="noopener noreferrer"
      standalone
      color="secondary"
      aria-label={`${pr.comments} comments`}
    >
      <Flex align="center" gap="1">
        <RiChat3Line size={16} aria-hidden />
        {pr.comments}
      </Flex>
    </Link>
  </Flex>
);

const PullRequestList = ({ data }: { data: PullRequest[] }) => {
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const frame = {
    border: '1px solid var(--bui-border-2)',
    borderRadius: 6,
    backgroundColor: 'var(--bui-bg-app)',
  };
  if (data.length === 0) {
    return (
      <Flex justify="center" py="4">
        <Text variant="body-medium" color="secondary">
          No open pull requests.
        </Text>
      </Flex>
    );
  }
  return (
    <Box style={frame}>
      {data.slice(offset, offset + pageSize).map(pr => (
        <PullRequestRow key={pr.id} pr={pr} />
      ))}
      <Box px="2" py="2">
        <TablePagination
          pageSize={pageSize}
          pageSizeOptions={[5, 10, 25]}
          offset={offset}
          totalCount={data.length}
          hasPreviousPage={offset > 0}
          hasNextPage={offset + pageSize < data.length}
          onPreviousPage={() => setOffset(o => Math.max(0, o - pageSize))}
          onNextPage={() => setOffset(o => o + pageSize)}
          onPageSizeChange={size => {
            setPageSize(size);
            setOffset(0);
          }}
        />
      </Box>
    </Box>
  );
};

export const OpenPullRequests = () => {
  const githubPrsApi = useApi(githubPullRequestsApiRef);
  const search = useCallback(
    () => searchPullRequests(githubPrsApi, OPEN_PULL_REQUESTS_QUERY),
    [githubPrsApi],
  );
  const prs = useAsyncValue(search);
  if (prs.loading) {
    return (
      <Flex direction="column" gap="2" role="progressbar" aria-label="Loading">
        {[0, 1, 2].map(i => (
          <Skeleton key={i} width="100%" height={36} rounded />
        ))}
      </Flex>
    );
  }
  if (prs.error) {
    return <Alert status="danger" icon title={prs.error.message} />;
  }
  return <PullRequestList data={prs.value ?? []} />;
};

/**
 * The home page "Your open pull requests" card on BUI, in place of Roadie's
 * Material-UI HomePageYourOpenPullRequestsCard. It asks GitHub the same way —
 * scmAuth credentials with the `repo` scope, then the plugin's own
 * searchPullRequest — and shows the same rows: status, repository and title,
 * "#N opened by", age and comment count, five a page.
 */
export const OpenPullRequestsCard = () => {
  const { signedIn, signIn } = useGitHubSignIn();

  return (
    <HomeCard title="Your open pull requests">
      {signedIn ? (
        <OpenPullRequests />
      ) : (
        <Flex align="center" justify="between" gap="4">
          <Text variant="body-large">
            You are not logged into GitHub. You need to be signed in to see the
            content of this card.
          </Text>
          <TooltipTrigger delay={300}>
            <Button variant="secondary" onPress={signIn}>
              Sign in
            </Button>
            <Tooltip>Sign in to Github</Tooltip>
          </TooltipTrigger>
        </Flex>
      )}
    </HomeCard>
  );
};
